if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelectorAll("[name*='id']").forEach(item => item.disabled = false);

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (formData.get('id') && formData.get('id[]')) {
            formData.delete('id');
          }
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // An accepted cart addition must never be submitted a second time just
        // because its preview failed. Retrying only reloads that same preview.
        const addition = this.pendingPreview
          ? Promise.resolve(this.pendingPreview.response)
          : fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
        return addition
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.pendingPreview ||= { response, formData };
            response = await this.syncRelatedFees(response, this.pendingPreview.formData);

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              await new Promise((resolve, reject) => {
                document.body.addEventListener('modalClosed', () => {
                  setTimeout(() => {
                    try { this.cart.renderContents(response); resolve(); }
                    catch (error) { reject(error); }
                  });
                }, { once: true });
                quickAddModal.hide(true);
              });
            } else {
              this.cart.renderContents(response);
            }
            this.pendingPreview = null;
            if (this.previewRetryLabel != null) {
              this.submitButton.querySelector('span').textContent = this.previewRetryLabel;
              this.previewRetryLabel = null;
            }
            // An unrelated subscriber must not undo an already opened preview.
            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            } catch (error) { console.error(error); }
          })
          .catch((e) => {
            console.error(e);
            if (this.pendingPreview) {
              this.pendingPreview.retry = true;
              this.handleErrorMessage('Der Artikel liegt bereits im Warenkorb. Die Vorschau konnte nicht geladen werden. Bitte klicke auf „Vorschau erneut laden“.');
              const label = this.submitButton.querySelector('span');
              if (label) {
                this.previewRetryLabel ??= label.textContent;
                label.textContent = 'Vorschau erneut laden';
              }
            } else {
              this.handleErrorMessage(e.message || 'Der Artikel konnte nicht hinzugefügt werden. Bitte versuche es erneut.');
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      async syncRelatedFees(response, formData) {
        if (!formData.get('properties[_related_cover]') && !formData.get('properties[_related_servicefee]')) return response;
        if (typeof validateLineItemsRelatedCover !== 'function') {
          throw new Error('Die Zusatzkosten konnten nicht aktualisiert werden. Bitte prüfe den Warenkorb.');
        }
        const result = await validateLineItemsRelatedCover();
        const variantId = formData.get('id') || formData.get('id[]');
        const addedItem = response.key ? response : response.items?.find(item => String(item.variant_id || item.id) === String(variantId));
        let currentItem = result.cart.items.find(item => item.key === addedItem?.key);
        if (!currentItem) {
          const properties = Array.from(formData).flatMap(([name, value]) => {
            const match = name.match(/^properties\[(.+)\]$/);
            return match && typeof value === 'string' ? [[match[1], value]] : [];
          });
          const candidates = result.cart.items.filter(item =>
            String(item.variant_id || item.id) === String(variantId) &&
            properties.every(([name, value]) => String(item.properties?.[name] ?? '') === value));
          if (candidates.length === 1) currentItem = candidates[0];
        }
        if (!currentItem) throw new Error('Der Artikel konnte der Warenkorb-Vorschau nicht eindeutig zugeordnet werden.');
        const preview = { ...response, key: currentItem.key, id: currentItem.id };
        if (!result.changed && !this.pendingPreview?.retry && response.key === currentItem.key && response.sections) return preview;

        // cart/add rendered its sections before the related quantities were
        // updated. Fetch all preview sections from the final cart together.
        const sectionIds = this.cart.getSectionsToRender().map(section => section.id);
        // Request rendered cart fragments without Accept: application/json.
        // Shopify otherwise returns cart/product data instead of the requested
        // section markup. This background request never navigates the page.
        const url = new URL(window.routes.cart_url, window.location.origin);
        url.searchParams.set('sections', sectionIds.join(','));
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const refreshed = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
            if (!refreshed.ok) throw new Error('Die Warenkorb-Vorschau konnte nicht aktualisiert werden.');
            const sections = await refreshed.json();
            // A missing optional header must not discard a valid product preview.
            if (typeof sections['cart-icon-bubble'] !== 'string' && typeof response.sections?.['cart-icon-bubble'] === 'string') {
              sections['cart-icon-bubble'] = response.sections['cart-icon-bubble'];
            }
            if (sectionIds.some(id => typeof sections[id] !== 'string')) throw new Error('Die Warenkorb-Vorschau konnte nicht aktualisiert werden.');
            return { ...preview, sections };
          } catch (error) {
            if (attempt === 1) throw error;
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
